<script setup>
import { computed } from "vue"

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [String, Number],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  iconComponent: {
    type: Object,
    default: null,
  },
  iconName: {
    type: String,
    default: null,
  },
  cardClass: {
    type: String,
    default: "",
  },
  titleClass: {
    type: String,
    default: "text-sm",
  },
  valueClass: {
    type: String,
    default: "text-lg font-semibold tracking-wide dark:bg-clip-text dark:text-transparent dark:bg-accent-gradient",
  },
  iconClass: {
    type: String,
    default: "h-8 w-8",
  },
  formatValue: {
    type: Boolean,
    default: false,
  },
})

const displayValue = computed(() => {
  if (props.value === null || props.value === undefined) return null
  if (typeof props.value === "number" && !props.formatValue) {
    return props.value.toLocaleString()
  }
  return props.value
})
</script>

<template>
  <div :class="['stat-card group', cardClass]">
    <div class="stat-card-inner">
      <v-icon
        v-if="iconName"
        :name="iconName"
        :class="[iconClass, 'drop-shadow-sm text-base-content/15 dark:text-base-content/10 group-hover:text-white dark:group-hover:text-accent transition-colors duration-300']"
      />
      <component
        :is="iconComponent"
        v-else-if="iconComponent"
        :class="[iconClass, 'drop-shadow-sm text-base-content/15 dark:text-base-content/10 group-hover:text-white dark:group-hover:text-accent transition-colors duration-300']"
      />
      <div class="flex flex-col gap-1 leading-1">
        <p :class="titleClass">{{ title }}</p>
        <p class="h-8 flex items-center">
          <span v-if="loading" class="text-base-content/25">
            <v-icon
              class="ov-spin"
              name="ri-loader-5-line"
              height="1em"
              width="1em"
              scale="1"
            />
          </span>
          <span
            v-else
            :class="valueClass"
          >
            {{ displayValue }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stat-card {
  @apply bg-zinc-100 border-zinc-300 shadow-none border dark:border-none dark:border-transparent relative overflow-hidden dark:shadow-md dark:bg-gradient-to-r dark:from-base-200 dark:to-base-300 ring-0 rounded-md;
}

.stat-card:hover {
  @apply bg-primary;
}

.stat-card:hover .stat-card-inner {
  @apply text-white dark:text-base-content;
}

.stat-card-inner {
  @apply flex items-center gap-5 py-4 px-6 relative z-10 dark:text-base-content text-base-content;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 10rem;
  right: -1.5rem;
  height: 100%;
  background-repeat: repeat-y;
  background-position: right center;
  opacity: 0.15;
  z-index: 0;
  @apply bg-base-100/40 skew-x-12 dark:hidden hover:bg-base-100/60;
}

.stat-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-repeat: repeat-y;
  background-position: right center;
  opacity: 0;
  z-index: 1;
}

.dark .stat-card::after {
  opacity: 0.05;
}

.stats-card-1::after {
  @apply !bg-[url('@/assets/images/stats-card-blob1.svg')];
}

.stats-card-2::after {
  @apply !bg-[url('@/assets/images/stats-card-blob2.svg')];
}

.stats-card-3::after {
  @apply !bg-[url('@/assets/images/stats-card-blob3.svg')];
}

.stat-card > div > p:first-child {
  @apply text-sm text-neutral-content/60 leading-[0.55rem];
}

.stat-card > div > p:last-child {
  @apply font-bold text-sm tracking-wider;
}
</style>